import React, { useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ListItemButton, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import DatePickerMui from '../../common/controls/DatePickerMui';
import PetAutocomplete from '../../common/controls/PetAutocomplete';
import AppointmentStatusSelect from '../../history/controls/AppointmentStatusSelect';
import UserAutocomplete from '../../common/controls/UserAutocomplete';
import { isEarlierDate } from '../../../utils/isEarlierDate';
import { daysBetweenDates } from '../../../utils/daysBetweenDates';
import { getAppointmentReport } from '../../../api/reportService';
import ReportDataGridModal from './ReportDataGridModal';
import AppointmentReportDataGrid from '../lists/AppointmentReportDataGrid';
import { TextField } from '@mui/material';

export default function AppointmentReport({mode = 'list'}) {
  const { t } = useTranslation('common'); 
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSort(['date', 'desc']);
    setDateFrom("");
    setDateTo("");
    setPetId("");
    setUserId("");
    setStatusId("");
    setRemarks("");
  };

  const { showSnackbar } = useSnackbar();

  const [sort, setSort] = useState(['date', 'desc']);

  const [viewOpen, setViewOpen] = React.useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const onInvoiceDateFromChange = (date) => {
    setDateFrom(date);
  };

  const onInvoiceDateToChange = (date) => {
    setDateTo(date);
  };

  const [petId, setPetId] = useState("");
  const [userId, setUserId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [remarks, setRemarks] = useState("");

  const handleStatusSelect = (selectedStatus) => {
      setStatusId(selectedStatus);
    }

  const fetchData = async () => {
    if (!dateFrom.trim()) {
      showSnackbar(t('reports.messages.date_from_required'), "warning");
      return;
    }
    if (!dateTo.trim()) {
      showSnackbar(t('reports.messages.date_to_required'), "warning");
      return;
    }
    if (!isEarlierDate(dateFrom, dateTo)) {
      showSnackbar(t('reports.messages.date_from_not_earlier'), "warning");
      return;
    }
    if (daysBetweenDates(dateFrom, dateTo) > 365) {
      showSnackbar(t('reports.messages.date_range_too_large'), "warning");
      return;
    }
    try {
      setFetchingData(true)
      const response = await getAppointmentReport(true, dateFrom, dateTo, petId, userId, statusId, remarks );
      // console.log(response);
      setReportData(response || null);
      setViewOpen(true);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setFetchingData(false);
    }
  }; 

  return (
    <React.Fragment>
      {mode === 'list' ? 
        <ListItem>
          <ListItemButton onClick={handleClickOpen}>
          <ListItemIcon>
            <CalendarMonthIcon fontSize='large' color='secondary'/>
          </ListItemIcon>
          <ListItemText 
            primary="Appointments"
            secondary="generate appointments report"
          />
          </ListItemButton>
        </ListItem>
      : null }
      {mode === 'button' ? 
        <Button onClick={handleClickOpen}>Appointments Report</Button>
      : null }

      <Dialog onClose={handleClose} open={open} disableRestoreFocus>

        <DialogTitle>
        <Stack spacing={1}>
          <Typography variant="h6">Appointments Report</Typography>
        </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <>
            <DatePickerMui
              label='Date From'
              onChange={onInvoiceDateFromChange}
            />
            <DatePickerMui
              label='Date To'
              onChange={onInvoiceDateToChange}
            />
            <PetAutocomplete
              label="Pet"
              value={petId}
              onChange={(id) => setPetId(id)} 
            />
            <UserAutocomplete
              label="Attending Doctor"
              value={userId}
              onChange={(id) => setUserId(id)} 
            />
            <AppointmentStatusSelect 
              statusId={statusId} 
              onSelect={handleStatusSelect}
              disabledOptions={[]}
            />
            <TextField
              label="Remarks"
              variant="outlined"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              fullWidth
              multiline
              rows={1}
            />
            <br />
            <br />
            <Button
              variant='outlined'
              fullWidth
              onClick={fetchData}
            >
              Generate Report
            </Button>
          </>
        </DialogContent>

        <DialogActions>
          <div style={{flex: '1 0 0'}} />
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
        <ReportDataGridModal 
          title='Appointments Report'
          reportData={reportData}
          reportDataGrid={<AppointmentReportDataGrid data={reportData?.data} />}
          open={viewOpen}
          onClose={() => {
            setViewOpen(false);
          }}
        />
      </Dialog>
    </React.Fragment>
  );
}